import type { NoteEventDraft } from './state/loggerDraftTypes';
import type { TimelineRow } from './timelineRows';

// Merge the set-event rows (buildTimelineRows) and the standalone note events
// into ONE chronological feed (next/timeline-logger.md §9, Solution C). The
// note is its own moment in the stream — rendered as a mini-card interleaved
// by time, "as if we'd logged an exercise" (Antenore 2026-06-04). Pure; no
// React, no platform API — RNW-safe and unit-testable.
//
// FUTURE ([[project_jl_timeline_as_feed]]): this discriminated FeedItem is the
// seed of the shareable event feed — new event kinds (coach comment, Bob clip)
// slot in as extra union members without touching set/note rendering.

export type FeedItem =
  | { type: 'set'; at: string; key: string; set: TimelineRow }
  | { type: 'note'; at: string; key: string; note: NoteEventDraft };

export function buildTimelineFeed(
  setRows: readonly TimelineRow[],
  noteEvents: readonly NoteEventDraft[],
): FeedItem[] {
  const items: FeedItem[] = [
    ...setRows.map(
      (set): FeedItem => ({
        type: 'set',
        at: set.completedAt,
        key: `set-${set.key}`,
        set,
      }),
    ),
    ...noteEvents.map(
      (note): FeedItem => ({
        type: 'note',
        at: note.performedAt,
        key: `note-${note.id}`,
        note,
      }),
    ),
  ];
  // Oldest first (matches the set-row order + the "started" marker on top).
  // Stable sort (ES2019+/Hermes): at an equal instant, the set keeps its
  // place before a note jotted right after it — the natural reading order.
  items.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  return items;
}
